/**
 * utils/geo.js
 *
 * Pure geo / GeoJSON utility functions shared across the app.
 * No React or browser dependencies — safe to import anywhere.
 */

// ─── Numeric helpers ──────────────────────────────────────────────────────────

/**
 * Round a number to 6 decimal places (≈ 11 cm precision in lat/lng).
 * @param {number} n
 * @returns {number}
 */
export function round6(n) {
   return Math.round(n * 1e6) / 1e6;
}

// ─── Distance ─────────────────────────────────────────────────────────────────

/**
 * Haversine great-circle distance in **meters** between two lat/lng pairs.
 *
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number}
 */
export function haversineDistance(lat1, lng1, lat2, lng2) {
   const R = 6371000;
   const toRad = (d) => (d * Math.PI) / 180;
   const dLat = toRad(lat2 - lat1);
   const dLng = toRad(lng2 - lng1);
   const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
   return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ─── Circle geometry ──────────────────────────────────────────────────────────

/**
 * Returns the point on a circle's border that is nearest to the user position.
 *
 * Uses the same flat-earth approximation as `buildCircleGeoJSON` so the
 * endpoint lands exactly on the visual polygon drawn on the map.
 *
 * @param {number} userLat
 * @param {number} userLng
 * @param {number} centerLat  Circle center latitude
 * @param {number} centerLng  Circle center longitude
 * @param {number} radiusM    Circle radius in meters
 * @returns {{ lat: number, lng: number }}
 */
export function nearestBorderPoint(userLat, userLng, centerLat, centerLng, radiusM) {
   const R = 6371000;

   // Flat-earth degree extents matching buildCircleGeoJSON
   const dLat = (radiusM / R) * (180 / Math.PI);
   const dLng = (radiusM / (R * Math.cos((centerLat * Math.PI) / 180))) * (180 / Math.PI);

   // Direction from center to user in the normalised ellipse space
   const nx = (userLng - centerLng) / dLng;
   const ny = (userLat - centerLat) / dLat;
   const len = Math.sqrt(nx * nx + ny * ny);

   if (len === 0) return { lat: centerLat + dLat, lng: centerLng };

   // Scale the unit direction back to geographic degrees
   return {
      lat: centerLat + dLat * (ny / len),
      lng: centerLng + dLng * (nx / len),
   };
}

// ─── GeoJSON builders ─────────────────────────────────────────────────────────

/**
 * Build a GeoJSON FeatureCollection containing a single polygon approximating
 * a circle around `(lat, lng)` with the given radius.
 *
 * Uses a flat-earth (equirectangular) approximation which is accurate enough
 * for small radii (< 50 km) and is consistent with `nearestBorderPoint`.
 *
 * @param {number} lng
 * @param {number} lat
 * @param {number} radiusMeters
 * @param {number} [steps=64]   Number of polygon vertices
 * @returns {GeoJSON.FeatureCollection}
 */
export function buildCircleGeoJSON(lng, lat, radiusMeters, steps = 64) {
   if (!radiusMeters || radiusMeters <= 0) {
      return { type: "FeatureCollection", features: [] };
   }
   const R = 6371000;
   const dLat = (radiusMeters / R) * (180 / Math.PI);
   const dLng = (radiusMeters / (R * Math.cos((lat * Math.PI) / 180))) * (180 / Math.PI);

   const coords = [];
   for (let i = 0; i <= steps; i++) {
      const angle = (i / steps) * 2 * Math.PI;
      coords.push([lng + dLng * Math.cos(angle), lat + dLat * Math.sin(angle)]);
   }

   return {
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: { type: "Polygon", coordinates: [coords] } }],
   };
}

/**
 * Build a GeoJSON FeatureCollection containing a MultiLineString from the
 * provided line segments (user → each circle border).
 *
 * @param {Array<[[number, number], [number, number]]>} lines
 *   Each element is `[[userLng, userLat], [borderLng, borderLat]]`.
 * @returns {GeoJSON.FeatureCollection}
 */
export function buildGuideLinesGeoJSON(lines) {
   return {
      type: "FeatureCollection",
      features: lines.length
         ? [
              {
                 type: "Feature",
                 geometry: { type: "MultiLineString", coordinates: lines },
              },
           ]
         : [],
   };
}

// ─── Coordinate helpers ───────────────────────────────────────────────────────

/**
 * Normalise the `coordinates` prop accepted by the Map component:
 * always returns a (possibly empty) array of `{ latitude, longitude }` objects.
 *
 * @param {{ latitude: number, longitude: number } | Array<{ latitude: number, longitude: number }> | null | undefined} coordinates
 * @returns {Array<{ latitude: number, longitude: number }>}
 */
export function normalizeCoordinates(coordinates) {
   if (!coordinates) return [];
   if (Array.isArray(coordinates)) return coordinates.filter(Boolean);
   return [coordinates];
}
